// NIGHT-DIRECTSTAFF-2026-10-19 §5: the saved views as chips at the top of the Leads list. Tap a chip to apply it;
// "…" (or a long press on a phone) to rename or delete; "Save this view" names the current filters + search + sort.
import { useEffect, useRef, useState } from 'react';
import { isActive, suggestName, newViewId, MAX_VIEWS } from './lib/savedViews';

const chip = (on) => ({ borderRadius: 999, padding: '0 14px', minHeight: 44, fontSize: 13, fontWeight: 700, border: `1.5px solid ${on ? '#1d4ed8' : '#cbd5e1'}`,
  background: on ? '#1d4ed8' : 'white', color: on ? 'white' : '#0f172a' });
const small = { minWidth: 44, minHeight: 44, borderRadius: 999, border: '1.5px solid #cbd5e1', background: 'white', color: '#334155', fontWeight: 700 };
const btn = (bg, fg) => ({ background: bg, color: fg, borderRadius: 8, padding: '0 14px', minHeight: 44, fontSize: 13, fontWeight: 700, border: 0 });

/** current: snapshot() of the list now; onApply(state); api: makeViewsApi(...) or null (not signed in); sourceLabels: SOURCES */
export default function SavedViews({ current, onApply, api, toast = () => {}, sourceLabels = {} }) {
  const [views, setViews] = useState(null);
  const [naming, setNaming] = useState(null);    // { id|null, name }  -- null id = a new view
  const [menu, setMenu] = useState(null);        // the view id whose menu is open
  const [busy, setBusy] = useState(false);
  const press = useRef(null);
  useEffect(() => { let live = true; if (!api) { setViews([]); return undefined; }
    api.load().then((v) => live && setViews(v)).catch(() => live && setViews([])); return () => { live = false; }; }, [api]);

  const save = async (list, said) => {
    setBusy(true);
    try { await api.save(list); setViews(list); toast(said); } catch (e) { toast(`Not saved: ${e.message || 'the view could not be stored'}`); } finally { setBusy(false); }
  };
  const commitName = () => {
    const name = naming.name.trim().slice(0, 60);
    if (!name) return;
    if (naming.id) save(views.map((v) => (v.id === naming.id ? { ...v, name } : v)), 'View renamed');
    else save([...views, { id: newViewId(), name, state: current }], `Saved "${name}"`);
    setNaming(null); setMenu(null);
  };
  const del = (v) => { save(views.filter((x) => x.id !== v.id), `Deleted "${v.name}"`); setMenu(null); };
  const longPressStart = (v) => { press.current = setTimeout(() => setMenu(v.id), 550); };
  const longPressEnd = () => { clearTimeout(press.current); };

  if (!api || views === null) return null;
  return (
    <div data-testid="saved-views" role="group" aria-label="Saved views" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
      {views.map((v) => (
        <span key={v.id} style={{ display: 'inline-flex', gap: 2, alignItems: 'center' }}>
          <button className="nb" data-testid={`view-${v.id}`} aria-pressed={isActive(v, current)} style={chip(isActive(v, current))}
            onClick={() => { onApply(v.state); setMenu(null); }}
            onTouchStart={() => longPressStart(v)} onTouchEnd={longPressEnd} onTouchMove={longPressEnd}
            onContextMenu={(e) => { e.preventDefault(); setMenu(v.id); }}>{v.name}</button>
          <button className="nb" data-testid={`view-more-${v.id}`} aria-label={`More for ${v.name}`} aria-expanded={menu === v.id} onClick={() => setMenu(menu === v.id ? null : v.id)} style={small}>…</button>
          {menu === v.id && (
            <span data-testid={`view-menu-${v.id}`} style={{ display: 'inline-flex', gap: 4 }}>
              <button className="nb" data-testid={`view-rename-${v.id}`} disabled={busy} onClick={() => setNaming({ id: v.id, name: v.name })} style={btn('#f1f5f9', '#0f172a')}>Rename</button>
              <button className="nb" data-testid={`view-delete-${v.id}`} disabled={busy} onClick={() => del(v)} style={btn('#fee2e2', '#b91c1c')}>Delete</button>
            </span>
          )}
        </span>
      ))}
      {!naming && views.length < MAX_VIEWS && (
        <button className="nb" data-testid="view-save" disabled={busy} onClick={() => setNaming({ id: null, name: suggestName(current, { sourceLabels }) })}
          style={{ ...chip(false), borderStyle: 'dashed', color: '#1d4ed8' }}>+ Save this view</button>
      )}
      {naming && (
        <form data-testid="view-name-form" onSubmit={(e) => { e.preventDefault(); commitName(); }} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', flexBasis: '100%' }}>
          <label htmlFor="view-name" style={{ fontSize: 12, color: '#475569', fontWeight: 700 }}>{naming.id ? 'New name' : 'Name this view'}</label>
          <input id="view-name" data-testid="view-name" className="input" value={naming.name} maxLength={60} autoFocus
            onChange={(e) => setNaming({ ...naming, name: e.target.value })} style={{ minHeight: 44, fontSize: 14, flex: '1 1 180px' }} />
          <button className="nb" type="submit" data-testid="view-name-save" disabled={busy || !naming.name.trim()} style={btn('#1d4ed8', 'white')}>Save</button>
          <button className="nb" type="button" data-testid="view-name-cancel" onClick={() => setNaming(null)} style={btn('#f1f5f9', '#475569')}>Cancel</button>
        </form>
      )}
    </div>
  );
}
